import type { CSSProperties } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { consumerGuideUrl } from '../lib/consumerGuideLink';

/**
 * AdoptionChecklist — queue #660 §UAT-2026-07-09-R660 §R660-1/§R660-2.
 *
 * `GettingStartedPage` 의 `AdoptionPathTable`(3경로 비교) 다음, `CurationBundlePicker`
 * (㉰ zip) 앞에 삽입되는 3경로별 단계 체크리스트. 각 단계 = 체크박스 + 라벨 + 기존 섹션
 * 앵커 링크("이동 →"). 체크 상태는 `localStorage`(키 `eraf.adoptionChecklist.<path>`)에
 * 저장하고 경로별 진행률 바를 렌더한다.
 *
 * 재구현 금지(§R660-2) — 신규 상태관리 라이브러리 도입 없이 로컬 `useState` + localStorage
 * try/catch 만 사용. 체크박스는 네이티브 `<input type="checkbox">`.
 *
 * 5상태(§R660-5): 정적 + localStorage 라 **default 상시** · **error 는 localStorage 접근
 * 불가(프라이빗 모드/쿼터/차단) 시에만 국소** — try/catch 로 인메모리 상태 폴백(체크리스트는
 * 정상 동작하되 새로고침 시 미복원). loading/empty/dialog 는 발생하지 않는다.
 */

type AdoptionPathKey = 'nexus' | 'remake' | 'zip';

type StepAnchor = { type: 'hash'; href: string } | { type: 'route'; to: string };

interface ChecklistStepDef {
  id: string;
  labelKey: string;
  anchor: StepAnchor;
}

const STORAGE_PREFIX = 'eraf.adoptionChecklist.';

const GUIDE_URL = consumerGuideUrl();

const NEXUS_STEPS: ChecklistStepDef[] = [1, 2, 3, 4, 5].map((n) => ({
  id: `step${n}`,
  labelKey: `gettingStartedGuide.checklist.nexus.step${n}`,
  anchor: { type: 'hash', href: '#gsg-quickstart' },
}));

const REMAKE_STEPS: ChecklistStepDef[] = [
  { id: 'step1', labelKey: 'gettingStartedGuide.checklist.remake.step1', anchor: { type: 'hash', href: '#gsg-access-heading' } },
  { id: 'step2', labelKey: 'gettingStartedGuide.checklist.remake.step2', anchor: { type: 'route', to: GUIDE_URL } },
  { id: 'step3', labelKey: 'gettingStartedGuide.checklist.remake.step3', anchor: { type: 'route', to: GUIDE_URL } },
  { id: 'step4', labelKey: 'gettingStartedGuide.checklist.remake.step4', anchor: { type: 'hash', href: '#gsg-walkthrough-heading' } },
  { id: 'step5', labelKey: 'gettingStartedGuide.checklist.remake.step5', anchor: { type: 'hash', href: '#gsg-walkthrough-heading' } },
  { id: 'step6', labelKey: 'gettingStartedGuide.checklist.remake.step6', anchor: { type: 'hash', href: '#gsg-walkthrough-heading' } },
];

const ZIP_STEPS: ChecklistStepDef[] = [
  { id: 'step1', labelKey: 'gettingStartedGuide.checklist.zip.step1', anchor: { type: 'hash', href: '#gsg-bundle' } },
  { id: 'step2', labelKey: 'gettingStartedGuide.checklist.zip.step2', anchor: { type: 'hash', href: '#gsg-bundle' } },
  { id: 'step3', labelKey: 'gettingStartedGuide.checklist.zip.step3', anchor: { type: 'hash', href: '#gsg-bundle' } },
  { id: 'step4', labelKey: 'gettingStartedGuide.checklist.zip.step4', anchor: { type: 'hash', href: '#gsg-bundle' } },
  { id: 'step5', labelKey: 'gettingStartedGuide.checklist.zip.step5', anchor: { type: 'route', to: GUIDE_URL } },
];

const PATHS: { key: AdoptionPathKey; steps: ChecklistStepDef[] }[] = [
  { key: 'nexus', steps: NEXUS_STEPS },
  { key: 'remake', steps: REMAKE_STEPS },
  { key: 'zip', steps: ZIP_STEPS },
];

type CheckedMap = Record<string, boolean>;

/** localStorage 는 프라이빗 모드/쿼터 초과 시 throw 할 수 있다 — try/catch 로 감싸 빈 값 폴백. */
function readStoredChecklist(pathKey: AdoptionPathKey): CheckedMap {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${pathKey}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') return parsed as CheckedMap;
    return {};
  } catch {
    return {};
  }
}

/** write 실패(쿼터/차단)는 콘솔 경고만 남기고 인메모리 상태로 graceful 폴백한다(§R660-5). */
function writeStoredChecklist(pathKey: AdoptionPathKey, value: CheckedMap): void {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${pathKey}`, JSON.stringify(value));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[AdoptionChecklist] localStorage 저장 실패 — 인메모리로만 유지됩니다 (${pathKey})`, e);
  }
}

function removeStoredChecklist(pathKey: AdoptionPathKey): void {
  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${pathKey}`);
  } catch {
    // graceful — 인메모리 상태는 이미 초기화됨
  }
}

const pathCardStyle: CSSProperties = {
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-2)',
};

const stepRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--spacing-2)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  padding: 'var(--spacing-1) 0',
};

const resetButtonStyle: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  padding: 'var(--spacing-1) var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

export function AdoptionChecklist() {
  const { t } = useTranslation();

  const [checked, setChecked] = useState<Record<AdoptionPathKey, CheckedMap>>(() => ({
    nexus: readStoredChecklist('nexus'),
    remake: readStoredChecklist('remake'),
    zip: readStoredChecklist('zip'),
  }));

  const toggleStep = useCallback((pathKey: AdoptionPathKey, stepId: string) => {
    setChecked((prev) => {
      const pathState: CheckedMap = { ...(prev[pathKey] ?? {}) };
      pathState[stepId] = !pathState[stepId];
      writeStoredChecklist(pathKey, pathState);
      return { ...prev, [pathKey]: pathState };
    });
  }, []);

  const resetPath = useCallback((pathKey: AdoptionPathKey) => {
    setChecked((prev) => ({ ...prev, [pathKey]: {} }));
    removeStoredChecklist(pathKey);
  }, []);

  const progressByPath = useMemo(() => {
    const result: Record<AdoptionPathKey, { done: number; total: number; percent: number }> = {
      nexus: { done: 0, total: NEXUS_STEPS.length, percent: 0 },
      remake: { done: 0, total: REMAKE_STEPS.length, percent: 0 },
      zip: { done: 0, total: ZIP_STEPS.length, percent: 0 },
    };
    for (const { key, steps } of PATHS) {
      const done = steps.filter((s) => checked[key]?.[s.id]).length;
      const total = steps.length;
      result[key] = { done, total, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
    }
    return result;
  }, [checked]);

  return (
    <section id="gsg-checklist" aria-labelledby="gsg-checklist-heading">
      <h2
        id="gsg-checklist-heading"
        style={{ margin: '0 0 var(--spacing-1) 0', fontSize: 'var(--font-size-lg)', color: 'var(--color-text-primary)' }}
      >
        {t('gettingStartedGuide.checklist.heading')}
      </h2>
      <p style={{ margin: '0 0 var(--spacing-4) 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
        {t('gettingStartedGuide.checklist.intro')}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--spacing-4)' }}>
        {PATHS.map(({ key, steps }) => {
          const { done, total, percent } = progressByPath[key];
          const pathName = t(`gettingStartedGuide.checklist.path.${key}`);

          return (
            <div key={key} style={pathCardStyle} data-testid={`adoption-checklist-${key}`}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--color-text-primary)' }}>{pathName}</h3>
                <button
                  type="button"
                  onClick={() => resetPath(key)}
                  style={resetButtonStyle}
                  aria-label={`${t('gettingStartedGuide.checklist.reset')} — ${pathName}`}
                >
                  {t('gettingStartedGuide.checklist.reset')}
                </button>
              </div>

              <div
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={t('gettingStartedGuide.checklist.progressAria', { path: pathName, percent })}
                style={{
                  height: 8,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-muted)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${percent}%`,
                    background: 'var(--color-brand-primary)',
                    transition: 'width 0.2s ease',
                  }}
                />
              </div>
              <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('gettingStartedGuide.checklist.progress', { done, total, percent })}
              </p>

              <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
                {steps.map((step) => {
                  const inputId = `gsg-checklist-${key}-${step.id}`;
                  const isChecked = Boolean(checked[key]?.[step.id]);
                  return (
                    <li key={step.id} style={stepRowStyle}>
                      <label htmlFor={inputId} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer', flex: 1 }}>
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleStep(key, step.id)}
                        />
                        <span>{t(step.labelKey)}</span>
                      </label>
                      {step.anchor.type === 'hash' ? (
                        <a href={step.anchor.href} style={{ color: 'var(--color-info)', fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                          {t('gettingStartedGuide.checklist.jump')}
                        </a>
                      ) : (
                        <Link to={step.anchor.to} style={{ color: 'var(--color-info)', fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                          {t('gettingStartedGuide.checklist.jump')}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          );
        })}
      </div>
    </section>
  );
}
